import { getFCP, getLCP, getFID, getCLS } from 'web-vitals'
import * as utils from './utils'
import getBsInfo from './browser'

class FLogger {
  constructor(param) {
    const config = param

    // 参数校验
    this._configVerify(config)

    // 服务端 url 地址
    this.url = config.url

    // 项目名称
    this.appName = config.appName

    // 环境
    this.env = config.env

    // 崩溃日志
    this.crashErrUrl = config.crashErrUrl

    // uid
    this.uid = config.uid ? String(config.uid) : ''

    // cid
    this.cid = config.cid ? String(config.cid) : ''

    // gid
    this.gid = config.gid ? String(config.gid) : ''

    // 200状态返回数据中 可能出现的错误号键名列表
    this.errnoNameArr = config.errnoNameArr || []

    // 接口header中的错误码
    this.headerError = config.headerError || []

    // 是否自动记录未捕获错误
    this.autoLogError = config.autoLogError === undefined ? true : config.autoLogError

    // 是否自动记录Promise未捕获错误
    this.autoLogRejection = config.autoLogRejection === undefined ? true : config.autoLogRejection

    // 是否自动记录 ajax
    this.autoLogAjax = config.autoLogAjax === undefined ? true : config.autoLogAjax

    // 不需要自动记录的请求路径
    this.ajaxIgnorePath = config.ajaxIgnorePath || []
    this.ignoreApis = config.ignoreApis || []

    // 需要记录的性能日志字段，默认domLoading/domInteractive/loadEventEnd
    this.performKeys = config.performKeys || ['dl', 'di', 'lee']

    // 默认的间隔发送时间（毫秒）
    const defaultInterval = 10000

    // 间隔发送时间
    this.interval = config.interval || defaultInterval

    // 默认的最大请求出错次数
    const defaultMaxErrorReq = 5

    // 发送请求出错的最大次数，超过此次数则不再发送请求，但依然会记录请求到队列中
    this.maxErrorReq = config.maxErrorReq || defaultMaxErrorReq

    // 当前请求出错次数
    this.errorReq = 0

    // 日志队列
    this.pendingQueue = [] // 发送中的ajax请求，先进入等待队列
    this.logQueue = [] // 发送日志的队列

    // 发送日志请求的 xhr 对象
    this.xhr = null

    // xhr 原生 open 方法
    this.xhrOpen = XMLHttpRequest.prototype.open

    // xhr 原生 send 方法
    this.xhrSend = XMLHttpRequest.prototype.send

    // 唯一的reqId
    this.reqId = ''

    // 是否可以读写localstorage(无痕模式)
    this.isCanUseLocalStorage = false

    // 是否可以使用indexedDB
    this.isCanUseIndexedDB = false

    // indexedDB实例
    this.db = null

    // indexedDB store name
    this.storeName = 'fLoggerStore'

    // 初始化
    this._init()
      .then(() => {
        // console.log('初始化成功')
      })
      .catch(() => {
        // console.log('初始化失败')
      })

    getFCP(entry => {
      this._pushToQueue(this._getWebVitalsMsg('fcp', entry.value))
    })

    getLCP(entry => {
      this._pushToQueue(this._getWebVitalsMsg('lcp', entry.value))
    })

    getFID(entry => {
      this._pushToQueue(this._getWebVitalsMsg('fid', entry.value))
    })

    getCLS(entry => {
      this._pushToQueue(this._getWebVitalsMsg('cls', entry.value))
    })
  }

  /**
   * 检查config参数是否合法
   * @param config
   * @returns {boolean}
   * @private
   */
  _configVerify(config) {
    if (typeof config === 'object') {
      if (typeof config.url !== 'string') {
        throw new Error('fLogger初始化错误 - 构造函数的参数 url 必须是一个字符串！')
      } else if (typeof config.appName !== 'string') {
        throw new Error('fLogger初始化错误 - appName 必须是一个字符串')
      } else if (typeof config.env !== 'string') {
        throw new Error('fLogger初始化错误 - env 必须是一个字符串')
      } else {
        return true
      }
    } else {
      throw new Error('fLogger初始化错误 - 构造函数的参数格式不正确！')
    }
  }

  /**
   * 初始化方法
   *
   * @memberof fLogger
   */
  async _init() {
    // 自动记录异常
    this._exceptionHandler()

    // 自动记录 ajax 请求
    this._ajaxHandler()

    // 绑定页面加载事件 加载完毕添加performance进队列
    this._bindOnLoadPerformance()

    // 绑定页面卸载事件
    this._storageUnsentData()

    // 判断是否可以读写localstorage
    this._checkIsCanUseLocalStorage()

    // 通过打开数据库 判断是否可以读写indexedDB
    await this._checkIsCanUseIndexedDB()

    // 获取唯一请求id
    await this._getReqId()

    // 为了记录全ajax请求，将异步处理后置，导致有几条数据没有reqid
    // 此方法用于将reqid添加到数据中
    this._addReqId()

    // 加载之前未发送的历史日志
    await this._loadFromStorage()

    // 定时发送日志请求
    this.timer = setInterval(() => {
      this._send()
    }, this.interval)
  }

  /**
   * 为了记录全ajax请求，将异步处理后置，导致有几条数据没有reqid
   * 此方法用于将reqid添加到数据中
   * @private
   */
  _addReqId() {
    this.pendingQueue.forEach(i => {
      i.identify = i.identify || this.reqId
    })
    this.logQueue.forEach(i => {
      i.identify = i.identify || this.reqId
    })
  }

  /**
   * 绑定页面加载事件 加载完毕添加performance进队列
   * @private
   */
  _bindOnLoadPerformance() {
    window.addEventListener('load', () => {
      // 避免onload后loadEventEnd还是为0的情况
      setTimeout(() => {
        this._pushToQueue(this._getPerformMsg())
      }, 0)
    })
  }

  /**
   * 创建objectStore
   * @private
   */
  _createObjectStore() {
    const storeName = this.storeName
    const keyPath = 'key'
    const db = this.db
    if (!db.objectStoreNames.contains(storeName)) {
      db.createObjectStore(storeName, { keyPath: keyPath })
    }
  }

  /**
   * indexedDB初始化
   * @private
   */
  _initIndexedDB() {
    return new Promise((resolve, reject) => {
      const self = this
      const dbName = 'fLogger'
      const openRequest = window.indexedDB.open(dbName) // 创建一个打开请求
      openRequest.onerror = function () {
        reject(new Error('数据库打开失败'))
      }
      openRequest.onsuccess = function (e) {
        self.db = e.target.result
        resolve()
      }
      openRequest.onupgradeneeded = function (e) {
        self.db = e.target.result
        self._createObjectStore()
      }
    })
  }

  /**
   * 通过数据库初始化 检查是否可以使用indexedDB
   * @private
   */
  _checkIsCanUseIndexedDB() {
    return new Promise(resolve => {
      this._initIndexedDB()
        .then(() => {
          this.isCanUseIndexedDB = true
          resolve()
        })
        .catch(() => {
          this.isCanUseIndexedDB = false
          resolve()
        })
    })
  }

  /**
   * 写入数据同步版
   * @private
   */
  _setItemSync(key, data) {
    if (this.isCanUseIndexedDB) {
      const objectStore = this.db
        .transaction(this.storeName, 'readwrite')
        .objectStore(this.storeName)
      objectStore.put({ key, data })
    } else if (this.isCanUseLocalStorage) {
      localStorage.setItem(key, JSON.stringify(data))
    } else {
      // console.log('无法使用本地储存')
    }
  }

  /**
   * 封装的 从本地储存写入数据的方法
   * @private
   */
  async _setItem(key, data) {
    const self = this
    if (this.isCanUseIndexedDB) {
      const isHasData = (await this._getItem(key)) !== undefined // 判断是否已经有值了
      const objectStore = this.db
        .transaction(this.storeName, 'readwrite')
        .objectStore(this.storeName)
      const request = isHasData ? objectStore.put({ key, data }) : objectStore.add({ key, data })
      return new Promise(resolve => {
        request.onerror = async function () {
          self.isCanUseIndexedDB = false
          await self._setItem(key, data)
          resolve()
        }
        request.onsuccess = function () {
          resolve()
        }
      })
    } else if (this.isCanUseLocalStorage) {
      localStorage.setItem(key, JSON.stringify(data))
      return Promise.resolve()
    } else {
      // 无法使用本地储存
      return Promise.reject(new Error('无法使用本地储存'))
    }
  }

  /**
   * 封装的 从本地储存获取信息的方法
   * @private
   */
  async _getItem(key) {
    const self = this
    if (this.isCanUseIndexedDB) {
      const objectStore = this.db
        .transaction(this.storeName, 'readwrite')
        .objectStore(this.storeName)
      const request = objectStore.get(key)
      return new Promise(resolve => {
        request.onerror = async function () {
          self.isCanUseIndexedDB = false
          resolve(await self._getItem(key))
        }
        request.onsuccess = function (e) {
          resolve(e.target.result && e.target.result.data)
        }
      })
    } else if (this.isCanUseLocalStorage) {
      return Promise.resolve(JSON.parse(localStorage.getItem(key)))
    } else {
      // 无法使用本地储存
      return Promise.reject(new Error('无法使用本地储存'))
    }
  }

  /**
   * 获取或者生成唯一请求id，前后端分离项目，该值为浏览器唯一标记
   *
   * @memberof fLogger
   */
  async _getReqId() {
    // 检测是否服务端写入了 uuid
    const dom = document.querySelector('[name="_reqId"]')
    if (dom) {
      this.reqId = dom.content
    }

    if (!this.reqId) {
      try {
        this.reqId = (await this._getItem('fLogger-reqId')) || ''
      } catch (e) {}
    }
    if (!this.reqId) {
      // 如果不存在 reqId，说明这是一个纯前端的页面，就自己生成一个 reqId
      this.reqId = this._UuidMaker()
      // 将reqId写入localstorage中 以便从客户端维度识别用户
      try {
        await this._setItem('fLogger-reqId', this.reqId)
      } catch (e) {}
    }
  }

  _UuidMaker() {
    // UUID 生成 https://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript/8809472#8809472
    let time = Date.now()
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      // 使用更高精度的时间
      time += performance.now()
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
      const rand = (time + Math.random() * 16) % 16 | 0
      time = Math.floor(time / 16)
      return (char === 'x' ? rand : (rand & 0x3) | 0x8).toString(16)
    })
  }

  /**
   * 是否可以读写localstorage
   *
   * @returns
   * @memberof fLogger
   */
  _checkIsCanUseLocalStorage() {
    try {
      const testKey = 'fLogger-test'
      window.localStorage.setItem(testKey, '1')
      window.localStorage.removeItem(testKey)
      this.isCanUseLocalStorage = true
    } catch (error) {
      this.isCanUseLocalStorage = false
    }
  }

  /**
   * 从 localStorage 加载之前未发送的历史日志
   *
   * @memberof fLogger
   */
  async _loadFromStorage() {
    const lastData = (await this._getItem('fLogger')) || []
    if (lastData && lastData.length) {
      this.logQueue = lastData

      // 立即发送一次
      this._send()
    }
    // 清空队列
    try {
      await this._setItem('fLogger', null)
    } catch (e) {}
  }

  /**
   * 获取最外层的通用数据
   * @param type
   * @returns {*}
   * @private
   */
  _getCommonMsg(type) {
    const typeArr = ['request', 'error', 'security', 'perform', 'customer', 'metric', 'web-vitals']
    if (typeArr.indexOf(type) === -1) {
      // return console.log('类型不在范围内')
      return
    }
    const { bs_name = '', bs_tag = '', bs_version = '' } = getBsInfo()
    return {
      localTime: new Date().toISOString(),
      appName: this.appName, // 项目名
      env: this.env, // test/pre/online
      eventType: type, // request/error/security/perform/customer
      path: location.pathname + location.search + location.hash, // 当前页面路径
      pathname: location.pathname, // 当前页面路径，无参数
      identify: this.reqId || '', // uuid
      referrer: document.referrer, // 来源
      uid: this.uid ? String(this.uid) : '', // 用户uid
      cid: this.cid ? String(this.cid) : '', // 企业id
      gid: this.gid ? String(this.gid) : '', // 分组id
      userAgent: navigator.userAgent, // ua
      browserName: bs_name,
      browserTag: bs_tag,
      browserVersion: bs_version
    }
  }

  _getErrorMsg(type, errorEvent) {
    const errorContext = {
      type,
      filename: errorEvent.filename || undefined,
      lineNo: errorEvent.lineNo ? String(errorEvent.lineNo) : undefined,
      colNo: errorEvent.colNo ? String(errorEvent.colNo) : undefined,
      message: errorEvent.message || (errorEvent.error && errorEvent.error.message) || undefined,
      stack: errorEvent.stack || (errorEvent.error && errorEvent.error.stack) || undefined,
      name: (errorEvent.error && errorEvent.error.name) || undefined
    }

    if (type === 'promise' && errorEvent.reason) {
      errorContext.message = errorEvent.reason.message
      errorContext.stack = errorEvent.reason.stack
    }

    const msg = this._getCommonMsg('error')
    msg.errorContext = errorContext
    return msg
  }

  _getPerformMsg() {
    if (!window.performance || (!performance.timing && !performance.getEntries)) {
      // console.log('浏览器没有performance接口')
      return
    }
    const msg = this._getCommonMsg('perform')
    if (performance.timing) {
      const timing = performance.timing
      const performContext = {}
      const performKeyMap = {
        rs: 'redirectStart',
        re: 'redirectEnd',
        fs: 'fetchStart',
        dls: 'domainLookupStart',
        dle: 'domainLookupEnd',
        cs: 'connectStart',
        rcs: 'secureConnectionStart',
        ce: 'connectEnd',
        reqs: 'requestStart',
        ress: 'responseStart',
        ues: 'unloadEventStart',
        uee: 'unloadEventEnd',
        dl: 'domLoading',
        rese: 'responseEnd',
        di: 'domInteractive',
        dcles: 'domContentLoadedEventStart',
        dclee: 'domContentLoadedEventEnd',
        dc: 'domComplete',
        les: 'loadEventStart',
        lee: 'loadEventEnd'
      }
      const timeOrigin = timing.navigationStart || 0
      this.performKeys.forEach(key => {
        if (performKeyMap[key]) {
          const time = timing[performKeyMap[key]]
          performContext[key] = time ? time - timeOrigin : 0
        }
      })
      msg.performContext = performContext
    }
    if (performance.getEntries) {
      const entries = performance.getEntries()
      let cssTime = 0
      let cssNum = 0
      let jsTime = 0
      let jsNum = 0
      let fpt = 0
      let fcpt = 0
      entries.forEach(resource => {
        if (/\.css$/i.test(resource.name)) {
          cssTime += Math.floor(resource.duration) || 0
          cssNum++
        } else if (/\.js$/i.test(resource.name)) {
          jsTime += Math.floor(resource.duration) || 0
          jsNum++
        } else if (resource.name === 'first-paint') {
          fpt = Math.floor(resource.startTime) || 0
        } else if (resource.name === 'first-contentful-paint') {
          fcpt = Math.floor(resource.startTime) || 0
        }
      })
      msg.resourceContext = {
        css: cssNum ? Math.floor(cssTime / cssNum) : undefined,
        js: jsNum ? Math.floor(jsTime / jsNum) : undefined,
        fpt: fpt || undefined,
        fcpt: fcpt || undefined
      }
    }
    return msg
  }

  _getCustomerMsg(...args) {
    try {
      if (args.length > 5 || args.length < 1) {
        // console.log('自定义日志参数数量错误')
        return
      }
      const msg = this._getCommonMsg('customer')
      const projectContext = {}
      args.forEach((item, index) => {
        projectContext['param' + ++index] = String(item)
      })
      msg.projectContext = projectContext
      return msg
    } catch (e) {}
  }

  _getMetricMsg(metric, value) {
    metric = String(metric)
    value = Number(value)
    try {
      const msg = this._getCommonMsg('metric')
      msg.metricContext = {
        metric,
        value: value || 0
      }
      return msg
    } catch (e) {}
  }

  _getWebVitalsMsg(metric, value) {
    const msg = this._getCommonMsg('web-vitals')
    msg.webVitalsContext = {
      metric,
      value
    }
    return msg
  }

  /**
   * 自动记录异常
   *
   * @memberof fLogger
   */
  _exceptionHandler() {
    // 页面未捕获异常
    if (this.autoLogError) {
      window.addEventListener('error', err => {
        if (/chrome-extension:\/\//.test(err.filename)) {
          return
        }
        this._pushToQueue(this._getErrorMsg('error', err))
      })
    }

    // Promise 未捕获异常
    if (this.autoLogRejection) {
      window.addEventListener('unhandledrejection', err => {
        this._pushToQueue(this._getErrorMsg('promise', err))
      })
    }
  }

  /**
   * 解析 url
   * @param url
   * @returns {HTMLAnchorElement}
   * @private
   */
  _resolveUrl(url) {
    const link = document.createElement('a')
    link.href = url
    return link
  }

  /**
   * 自动记录 ajax 请求
   *
   * @memberof fLogger
   */
  _ajaxHandler() {
    if (!this.autoLogAjax) {
      return
    }
    const self = this

    // 重写 open 方法
    XMLHttpRequest.prototype.open = function (...args) {
      const method = args[0]
      const urlObj = self._resolveUrl(args[1])
      let pathname = urlObj.pathname || ''
      if (pathname[0] !== '/') {
        pathname = '/' + pathname
      }

      // 屏蔽需要忽略的接口
      if (
        !utils.ignoreByRule(urlObj.href, self.ignoreApis) &&
        !utils.ignoreByRule(pathname, self.ajaxIgnorePath)
      ) {
        // 组装要发送的json对象
        this.fLoggerMsg = {
          requestContext: {
            host: urlObj.host, // host名
            pathname, // 接口路径
            query: urlObj.search, // query参数
            method: method // 请求方式
          }
        }
      }
      self.xhrOpen.apply(this, args)
    }

    // 重写 send 方法
    XMLHttpRequest.prototype.send = function (data) {
      if (this.fLoggerMsg) {
        // 请求开始时间
        const startTime = new Date()

        if (data) {
          this.fLoggerMsg.requestContext.body =
            typeof data === 'string' ? data : JSON.stringify(data) // 请求体字符串
        }

        Object.assign(this.fLoggerMsg, self._getCommonMsg('request'))
        self.pendingQueue.push(this.fLoggerMsg)

        // 添加 超时事件
        this.addEventListener('timeout', function () {
          // 请求结束时间
          const endTime = new Date()

          this.fLoggerMsg.requestContext.responseTime = (endTime - startTime) / 1000 // 请求消耗时间
          self._pushToQueue(this.fLoggerMsg)
          _delPendingLogger.call(this)
        })

        // 添加 readystatechange 事件
        this.addEventListener('readystatechange', function () {
          if (this.readyState === XMLHttpRequest.DONE) {
            // 请求结束时间
            const endTime = new Date()

            // 请求耗时
            const costTime = (endTime - startTime) / 1000
            // 响应头
            const responseHeaders = this.getAllResponseHeaders()

            Object.assign(this.fLoggerMsg.requestContext, {
              statusCode: this.status, // 返回的状态码
              responseTime: costTime, // 请求消耗时间
              responseText: this.responseType === 'blob' ? '' : this.responseText // 响应，兼容blob类型
            })
            if (responseHeaders && self.headerError.length !== 0) {
              const arr = responseHeaders.split('\r\n')
              const headers = arr.reduce(function (acc, current) {
                const parts = current.split(': ')
                acc[parts[0]] = parts[1]
                return acc
              }, {})
              this.fLoggerMsg.requestContext.headers = {}
              self.headerError.map(item => {
                if (headers[item]) {
                  Object.assign(this.fLoggerMsg.requestContext.headers, {
                    [item]: headers[item] // 响应头
                  })
                }
              })
            }
            self._addErrNo(this.fLoggerMsg.requestContext) // 处理数据 解析错误号
            self._pushToQueue(this.fLoggerMsg)
            _delPendingLogger.call(this)
          }
        })
      }
      self.xhrSend.call(this, data)
    }

    // 将已经返回的xhr从pending队列移动到log队列中
    function _delPendingLogger() {
      const _this = this
      const logIndex = self.pendingQueue.indexOf(_this.fLoggerMsg)
      self.pendingQueue.splice(logIndex, 1)
    }
  }

  _addErrNo(context) {
    // 解析错误号
    try {
      if (!context) {
        return
      }
      const responseText = context.responseText
      if (!responseText) {
        return
      }
      const responseObj = JSON.parse(responseText)
      if (!responseObj) {
        return
      }
      const errnoNameArr = this.errnoNameArr
      for (let i = 0, length = errnoNameArr.length; i < length; i++) {
        if (Object.keys(responseObj).indexOf(errnoNameArr[i]) !== -1) {
          context.errno = String(responseObj[errnoNameArr[i]])
          break
        }
      }
    } catch (e) {}
  }

  /**
   * 页面卸载前检查是否还有未发送的日志
   *
   * @memberof fLogger
   */
  _storageUnsentData() {
    window.onbeforeunload = () => {
      if (!this.pendingQueue.length && !this.logQueue.length) {
        // 两个队列里都没有数据 不处理
        return
      }
      // 来不及等请求返回了，将pending队列的请求全部push到log队列中，一起发送日志
      this.logQueue = this.logQueue.concat(this.pendingQueue)
      this.pendingQueue = []
      if (this.errorReq >= this.maxErrorReq) {
        // 日志奔溃的情况 只考虑存
        if (this.isCanUseLocalStorage || this.isCanUseIndexedDB) {
          // 可以使用本地储存，则存入本地，下次进入页面时会自动发送一次日志
          this._setItemSync('fLogger', this.logQueue)
        } else {
          // 是无痕模式，只能尝试发送日志，成不成功就看造化了
          this._send()
        }
      } else {
        // 日志服务未崩溃 优先考虑sendBeacon
        if (
          !this._isIOS() &&
          navigator.sendBeacon &&
          navigator.sendBeacon(this.url, JSON.stringify(this.logQueue))
        ) {
          // zdao ios客户端有sendBeacon问题 ios统一存持久化
          // 如果安卓客户端支持sendBeacon，且预计能够成功发送数据，则清空队列
          this.logQueue = []
        } else if (this.isCanUseLocalStorage || this.isCanUseIndexedDB) {
          // 不支持sendBeacon，且可以使用本地储存，则存入本地，下次进入页面时会自动发送一次日志
          this._setItemSync('fLogger', this.logQueue)
        } else {
          // 是无痕模式，只能尝试发送日志，成不成功就看造化了
          this._send()
        }
      }
    }
  }

  /**
   * 检查是否是ios系统
   * @returns {boolean}
   * @private
   */
  _isIOS() {
    return /iphone|ipad|ios/i.test(navigator.userAgent)
  }

  /**
   * 发送日志请求
   *
   * @memberof fLogger
   */
  _send() {
    const logCount = this.logQueue.length
    if (!logCount) {
      return
    }
    // 如果存在 this.xhr，说明上一次的请求还没有结束，就又准备发送新的请求了，则直接终止上次请求
    if (this.xhr) {
      this.xhr.onreadystatechange = null
      this.xhr.abort()
    }

    try {
      this.xhr = new XMLHttpRequest()
      this.xhrOpen.call(this.xhr, 'POST', this.url, true)
      this.xhr.timeout = 10000
      // this.xhr.setRequestHeader('Content-Type', 'application/json; charset=utf-8')
      this.xhr.onreadystatechange = () => {
        if (this.xhr.readyState === XMLHttpRequest.DONE) {
          if (this.xhr.status >= 200 && this.xhr.status < 400) {
            // 日志发送成功，从队列中去除已发送的
            this.logQueue.splice(0, logCount)

            // 重置请求出错次数
            this.errorReq = 0
          } else {
            this._checkErrorReq()
          }
          this.xhr = null
        }
      }
      this.xhr.ontimeout = () => {
        this._checkErrorReq()
        this.xhr = null
      }
      this.xhrSend.call(this.xhr, JSON.stringify(this.logQueue))
    } catch (err) {
      this._checkErrorReq()
      this.xhr = null
    }
  }

  /**
   * 发送崩溃日志
   * @private
   */
  _sendCrashLog() {
    // 如果无crash，则不触发发送crash接口
    if (!this.crashErrUrl) {
      return
    }
    // 如果存在 this.xhr，说明上一次的请求还没有结束，就又准备发送新的请求了，则直接终止上次请求
    if (this.xhr) {
      this.xhr.onreadystatechange = null
      this.xhr.abort()
    }

    try {
      this.xhr = new XMLHttpRequest()
      this.xhrOpen.call(
        this.xhr,
        'GET',
        this.crashErrUrl + '?value=' + JSON.stringify(this._getCommonMsg('error')),
        true
      )
      this.xhr.timeout = 10000
      // this.xhr.setRequestHeader('Content-Type', 'application/json; charset=utf-8')
      this.xhr.onreadystatechange = () => {
        if (this.xhr.readyState === XMLHttpRequest.DONE) {
          // if (this.xhr.status >= 200 && this.xhr.status < 400) {
          //   console.log('崩溃日志发送成功')
          // } else {
          //   console.log('崩溃日志发送失败')
          // }
          this.xhr = null
        }
      }
      this.xhr.ontimeout = () => {
        // console.log('崩溃日志发送失败')
        this.xhr = null
      }
      this.xhrSend.call(this.xhr)
    } catch (err) {
      // console.log('崩溃日志发送失败')
      this.xhr = null
    }
  }

  /**
   * 检查请求出错次数
   *
   * @memberof fLogger
   */
  _checkErrorReq() {
    // 将出错次数 +1
    this.errorReq++

    // 超过最大次数则认为服务器不可用，停止定时器
    if (this.errorReq >= this.maxErrorReq) {
      clearInterval(this.timer)
      // console.log(`发送日志请求的连续失败次数过多，已停止发送日志。请检查日志接口 ${this.url} 是否正常！`)
      this._sendCrashLog()
    }
  }

  /**
   * 将日志添加到队列中
   * @param data
   * @private
   */
  _pushToQueue(data) {
    if (typeof data !== 'object' || !Object.keys(data)) {
      // return console.log('不能把空数据推进队列')
      return
    }
    this._dataValidate(data)
    this.logQueue.push(data)
  }

  /**
   * 请求日志数据过滤
   * @param data
   * @private
   */
  _dataValidate(data) {
    if (!data || !data.requestContext) {
      // 只检测请求类型的日志
      return
    }
    const requestContext = data.requestContext
    // 超长截取
    this._spliceLong(requestContext)
    // 敏感词替换
    this._replaceWord(requestContext)
  }

  /**
   * 敏感词替换
   * @param data
   * @private
   */
  _replaceWord(data) {
    const reg = /(password['"]*[:=]?)[\s\S]{5}/g
    data.body = data.body ? data.body.replace(reg, '$1*') : ''
    data.responseText = data.responseText ? data.responseText.replace(reg, '$1*') : ''
  }

  /**
   * 超长截取
   * @param data
   * @private
   */
  _spliceLong(data) {
    let LIMIT_LENGTH = 400 // 最长字符数
    // 白名单域名扩大截取长度
    const whiteList = ['api.geevisit.com', 'api.geetest.com']
    if (whiteList.indexOf(data.host) > -1) {
      LIMIT_LENGTH = 1000
    }
    if (data.body && data.body.length > LIMIT_LENGTH) {
      data.body = data.body.substring(0, LIMIT_LENGTH)
    }
    if (data.responseText && data.responseText.length > LIMIT_LENGTH) {
      data.responseText = data.responseText.substring(0, LIMIT_LENGTH)
    }
  }

  /**
   * 记录一条自定义日志
   * @param args
   */
  log(...args) {
    try {
      this._pushToQueue(this._getCustomerMsg(...args))
    } catch (e) {}
  }

  /**
   * 记录一条指标日志
   * @param metric string 指标类型
   * @param value number 指标的值
   */
  logMetric(metric, value) {
    try {
      this._pushToQueue(this._getMetricMsg(metric, value))
    } catch (e) {}
  }

  /**
   * 用于错误处理
   * @param err
   */
  errorCallback(err) {
    this._pushToQueue(this._getErrorMsg('error', err))
  }

  /**
   * 用于写入uid 用户id
   * 用于写入cid 企业id
   * 用于写入gid 分组id
   * @param cid
   * @param uid
   * @param gid
   */
  uidConfig(uid, cid, gid) {
    if (!uid) {
      // return console.log('uid设置失败')
      return
    }
    this.uid = uid ? String(uid) : ''
    this.cid = cid ? String(cid) : ''
    this.gid = gid ? String(gid) : ''
    this.pendingQueue.forEach(i => {
      // 如果有队列则把uid写入队列
      i.uid = i.uid || this.uid
      i.cid = i.cid || this.cid
      i.gid = i.gid || this.gid
    })
    this.logQueue.forEach(i => {
      // 如果有队列则把uid写入队列
      i.uid = i.uid || this.uid
      i.cid = i.cid || this.cid
      i.gid = i.gid || this.gid
    })
  }
}

if (window) {
  window.FLogger = FLogger
  const el = document.querySelector('#_flogger')
  if (el) {
    const config = JSON.parse(el.getAttribute('data-config'))
    if (config) {
      config.uid = document.querySelector('[name="_uid"]')
        ? document.querySelector('[name="_uid"]').content
        : 'none'
      window.fLog = new FLogger(config)
    }
  }
}
export default FLogger
